import React from "react"
import { Box, ChipFormField, Flex, Heading, ChipProps, Polymorphic } from "@indeed/ifl-components"
import { configSelector } from "../../../common/utils/config-slice"
import { useAppSelector } from "../../../common/utils/redux-helpers"
import { ifTrue } from "../../../common/utils/utils"
import { IAdditionalSelection, MultiSelectOpts } from "../feedback-form-model"
import { handleStickyBoxOverlap } from "../helpers"
import { IPreferenceItemProps, PreferenceItem } from "../preference-item"

export type IMultiselectPreferenceItemProps = Pick<
    IPreferenceItemProps,
    "category" | "selected" | "toggleSelect" | "bodyText" | "buttonAriaLabel"
> &
    MultiSelectOpts & {
        additionalSelectionSelectedHandler: (it: IAdditionalSelection, idx: number) => any
    }

type MultiselectPreferenceItemComponent = Polymorphic.Component<"li", IMultiselectPreferenceItemProps>

export const MultiselectPreferenceItem = ((props, _context) => {
    const {
        as = "li",
        category,
        selected,
        toggleSelect,
        bodyText,
        buttonAriaLabel,
        additionalSelections,
        additionalSelectionSelectedHandler,
        additionalSelectionsLabel,
    } = props
    const { proctorGroups } = useAppSelector(configSelector)
    const useTextLabelForCategory = proctorGroups.jsj_mrp_jcr_feedback_design_tst === 1

    return (
        <Box
            as={as}
            sx={{
                borderBlockEnd: "1px solid",
                borderBlockEndColor: "border.weak",
                marginBlockEnd: 0,
                marginBlockStart: 0,
                marginInlineEnd: useTextLabelForCategory ? 4 : 0,
                marginInlineStart: useTextLabelForCategory ? 4 : 0,
            }}
        >
            <PreferenceItem
                {...{
                    category,
                    selected,
                    toggleSelect,
                    bodyText,
                    buttonAriaLabel,
                    additionalSelectionsLabel: additionalSelections.length > 1 ? additionalSelectionsLabel : undefined,
                }}
                sx={{ borderBlockEnd: "none", marginBlockEnd: 0, marginBlockStart: 0, marginInlineEnd: 0, marginInlineStart: 0 }}
                as="div"
            />
            {ifTrue(selected && additionalSelections.length > 1, () => (
                <Flex
                    sx={{
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        marginBlockEnd: 6,
                        marginInlineStart: useTextLabelForCategory ? 0 : 4,
                    }}
                    onFocus={handleStickyBoxOverlap}
                >
                    <ChipFormField
                        size="md"
                        type="multiple"
                        name="additional-selections-chip"
                        label={
                            <Heading as="h3" level={3} sx={{ marginBlockEnd: 4 }}>
                                {additionalSelectionsLabel}
                            </Heading>
                        }
                        items={additionalSelections.map(
                            (it, i) =>
                                ({
                                    label: it.displayText,
                                    checked: it.selected,
                                    onChange: () => additionalSelectionSelectedHandler(it, i),
                                    sx: {
                                        label: {
                                            fontSize: 3,
                                            color: "foreground.strong",
                                            border: "1px solid",
                                            borderColor: "border",
                                            backgroundColor: "background.weaker",
                                            cursor: "pointer",
                                            // hides checkmark icon
                                            svg: { display: "none" },
                                        },
                                        // override "selected" colors
                                        "& > input:checked:hover+label": {
                                            backgroundColor: "background.inverse.strong",
                                            borderColor: "border",
                                        },
                                    },
                                } as ChipProps)
                        )}
                    />
                </Flex>
            ))}
        </Box>
    )
}) as MultiselectPreferenceItemComponent
